import * as angular from 'angular';
import * as moment from 'moment';

import { ComponentDefinition, Identifier, TagViewModel, TagDefinition, ImportedTagDefinition } from '../../models';
import { guidEmpty, tagsImported, tagSaved, groupIdentifierSeparator } from '../../common';
import {
    ITagDefinitionService,
    ItagsImportService,
    ILoggerService,
    IwidgetIntervalHelperService,
    IGroupTagsService,
    ITagsFilteringService
} from '../../services';

export interface IGroupTagsControllerScope {
    deleteAll: () => void;
    acquireAll: (beginDate, endDate) => void;
    refresh: () => void;
}

class GroupTagsController implements angular.IComponentController {
    addmethod: (param) => angular.IPromise<any>;
    exposedScope: IGroupTagsControllerScope;
    isLoading: boolean;
    loadingMessage: string;
    onAdd: () => void;
    selectedGroup: string;
    searchString: string;
    selectedItem: any;
    selectedIdentifier: Identifier;
    filteredTags: TagViewModel[];
    searchEnable = { tag: true, group: false };

    private _tags: TagViewModel[];
    private _selectedGuid: string;
    private _format: any;

    static $inject = [
        '$scope',
        'groupTagsService',
        'tagDefinitionService',
        'tagsFilteringService',
        'tagsImportService',
        'loggerService',
        'widgetIntervalHelperService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _groupTagsService: IGroupTagsService,
        private _tagDefinitionService: ITagDefinitionService,
        private _tagsFileringService: ITagsFilteringService,
        private _tagsImportService: ItagsImportService,
        private _loggerService: ILoggerService,
        private _widgetIntervalHelperService: IwidgetIntervalHelperService
    ) {
    }

    $onInit() {
        this._format = {
            variable: f => f.type,
            unit: f => f.unit,
            comment: f => f.shortComment,
            source: f => f.source
        };

        this._$scope.$watch(() => this.selectedGroup, () => {
            if (this.selectedGroup) {
                this.refresh();
            }
        });

        this._$scope.$watch(() => this.searchString, () => {
            this.filter();
        });

        this._$scope.$on(tagsImported, (event, data: ImportedTagDefinition[]) => {
            this.addToGroup(data);
        });

        this._$scope.$on(tagSaved, (event, data) => {
            this.refresh();
        });

        $('#acquireModal').on('shown.bs.modal', () => {
            this._selectedGuid = this.selectedItem.guid;
        });

        const self = this;
        this.exposedScope = {
            deleteAll: self.deleteAll(),
            acquireAll: (beginDate, endDate) => {
                const guids = self.filteredTags.map(t => t.guid);
                self.createAcquisitionTask(guids, beginDate, endDate);
            },
            refresh: () => { self.refresh(); }
        };
    }

    select(item: TagViewModel, $event) {
        this.selectedItem = item.tagDefinition;
        this.selectedIdentifier = item.uniqueIdentifier ? item.uniqueIdentifier : undefined;

        if ($event.stopPropagation) {
            $event.preventDefault();
        }
    }

    drop(data: any) {
        const name = data.itemId.replace('tag', '');
        const imported = this._tagsImportService.getByName(name);
        if (imported) {
            this.loadingMessage = 'Importing tags';
            this.isLoading = true;

            if (this.addmethod) {
                this.addmethod({ tagImport: imported }).then(() => {
                    this.isLoading = false;
                    this.addToGroup([imported]);
                });
            } else {
                this._tagsImportService.import(imported, []).then(() => {
                    this.isLoading = false;
                    this.addToGroup([imported]);
                });
            }
        }
    }

    unselect($event) {
        if (!$event.isDefaultPrevented()) {
            this.selectedItem = null;
        }

    }

    deleteTag(guid: string): void {
        this._tagDefinitionService.deleteTag(guid)
            .then(() => {
                this._loggerService.logSuccess('Tag successfully deleted.');
                this.selectedItem = null;
                this.refresh();
            })
            .catch(reason => {
                this._loggerService.logError('Failed to delete tag: ' + reason);
            });
    }

    acquire(beginDate: string, endDate: string): void {
        const guids = [this._selectedGuid];
        this.createAcquisitionTask(guids, beginDate, endDate);
    }

    getClass(tag: TagDefinition): any {
        let result: any = {};
        if (!tag.variableTypeGuid || tag.variableTypeGuid === guidEmpty
            || !tag.unitGuid || tag.unitGuid === guidEmpty) {
            result = {
                'panel-danger': true
            };
        } else if (tag.isReviewRequired) {
            result = {
                'panel-warning': true
            };
        } else {
            result = {
                'panel-primary': true
            };
        }
        if (this.selectedItem && tag.guid === this.selectedItem.guid) {
            result.bordered = true;
        }
        return result;

    }

    private createAcquisitionTask(guids: string[], beginDate: string, endDate: string) {
        beginDate = moment(beginDate).toISOString();
        endDate = moment(endDate).toISOString();
        const updatedInterval = this._widgetIntervalHelperService
            .getInterval(beginDate, endDate, {
                isEndDateIncluded: true,
                minPeriod: undefined,
                maxPeriod: undefined
            });
        beginDate = updatedInterval.beginMoment.toISOString();
        endDate = updatedInterval.endMoment.toISOString();
        this._groupTagsService.acquire(guids, beginDate, endDate)
            .then(() => {
                this._loggerService.logSuccess('Acquisition started.');
            })
            .catch(() => {
                this._loggerService.logError('Failed to start acquisition.');
            });
    }

    private deleteAll(): () => void {
        const self = this;
        return () => {
            const toDelete = self.filteredTags
                .filter(t => !t.identifierName)
                .map(t => t.guid);
            if (toDelete.length > 0) {
                self.loadingMessage = 'Deleting tags';
                self.isLoading = true;
                self._tagDefinitionService
                    .deleteTagList(toDelete)
                    .then(() => {
                        self._loggerService.logSuccess('Tags deleted');
                        self.isLoading = false;
                        self.refresh();
                    })
                    .catch(() => {
                        self._loggerService.logError('Failed to delete tags');
                        self.isLoading = false;
                    });
            } else {
                self._loggerService.log('No tag to delete');
            }

        };
    }

    private addToGroup(imported: ImportedTagDefinition[]) {
        if (this.selectedGroup && this.selectedGroup !== guidEmpty) {
            this.loadingMessage = 'Adding tags to group';
            this.isLoading = true;
            this._groupTagsService
                .addTagsToGroup(this.selectedGroup, imported.map(i => i.guid))
                .then(() => {
                    this._loggerService.logSuccess(`${imported.length} identifiers added to group.`);
                    this.isLoading = false;
                    if (this.onAdd) {
                        this.onAdd();
                    }
                    this.refresh();
                })
                .catch(() => {
                    this._loggerService.logError('Failed to add identifiers to group.');
                });
        } else {
            this.refresh();
        }
    }

    private refresh() {
        this.loadingMessage = 'Refreshing tags list';
        this.isLoading = true;
        this._groupTagsService
            .getViewModels(this.selectedGroup)
            .then(data => {
                this._tags = data;
                this.isLoading = false;
                this.filter();
            })
            .catch(() => {
                this._tags = [];
                this.isLoading = false;
                this.filter();
            });
    }

    private filter() {
        this.loadingMessage = 'Filtering tags list';
        this.isLoading = true;
        this.filteredTags = this._tagsFileringService.filterResults(this._tags, this.searchString, groupIdentifierSeparator, this._format);
        this.isLoading = false;
    }
}

export const wtGroupTags: ComponentDefinition = {
    componentId: 'wtGroupTags',
    controller: GroupTagsController,
    template: require('./groupTags.component.html'),
    controllerAs: 'vm',
    bindings: {
        selectedItem: '=',
        selectedIdentifier: '=',
        selectedGroup: '=',
        exposedScope: '=',
        addmethod: '&',
        onAdd: '&'
    }
};